import React, { FC } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  MenuItem,
  Select,
  Button,
  Typography,
  IconButton,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import { Image } from "cloudinary-react";
import PaintIcon from "@mui/icons-material/ImagesearchRoller";
import PaintLeadTimesModal from "../PaintLeadTimesModal";
import { styled } from "@mui/material/styles";
import useLeadTimesSelector from "./useLeadTimesSelector";
import { LeadTimeObj } from "./useLeadTimesSelector";

const StyledWrapDiv = styled("div")(({ theme }) => ({
  borderRadius: 5,
  backgroundColor: "white",
  boxShadow: "4px 4px 8px #888888",
  width: "45vw",
  marginBottom: 20,
  [theme.breakpoints.down("xl")]: {
    width: "100%",
  },
  [theme.breakpoints.down("lg")]: {
    width: "90vw",
  },
  position: "relative",
}));

const StyledSCDiv = styled("div")({
  border: "1px solid grey",
  borderRadius: 5,
  position: "relative",
});

const StyledLeadTimeRow = styled("div")({
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const StyledSCPositionTypo = styled(Typography)({
  width: 28,
  marginRight: 4,
  flexShrink: 0,
  textAlign: "right",
});

const StyledEditIcon = styled(IconButton)({
  position: "absolute",
  left: 20,
  top: 10,
});

const StyledPaintIcon = styled(IconButton)({
  position: "absolute",
  right: 20,
  bottom: 10,
});

const StyledUndoTypo = styled(Typography)({
  position: "absolute",
  left: 60,
  top: 20,
  fontWeight: "bold",
  fontSize: 14,
});

const StyledBtn = styled(Button)({
  position: "absolute",
  right: 60,
  top: 25,
  color: "white",
});

const StyledTypographyTitle = styled(Typography)(({ theme }) => ({
  fontWeight: "bold",
  [theme.breakpoints.down("lg")]: {
    fontSize: 24,
  },
}));

const StyledInput = styled("input")({
  height: 30,
  width: 100,
});

const StyledColDiv = styled("div")({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexDirection: "column",
  minHeight: 221,
});

const StyledRowDiv = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "row",
  padding: 20,
});

const StyledRowDiv3 = styled("div")({
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
  flexDirection: "row",
  textAlign: "center",
});

const StyledRowDiv2 = styled("div")(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  [theme.breakpoints.down("xl")]: {
    justifyContent: "space-around",
  },
  alignItems: "center",
  flexDirection: "row",
  marginLeft: 25,
  marginRight: 25,
  [theme.breakpoints.down("md")]: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    flexDirection: "column",
  },
}));

const StyledTypoHeader = styled(Typography)({
  textDecoration: "underline",
  paddingBottom: 10,
});

const StyledImageWrap = styled("div")({
  height: 132,
});

const LeadTimes = () => {
  const {
    publicIds,
    handleAdminLeadTimeUpdate,
    handleAdminPaintLeadTimeUpdate,
    changeToEditMode,
    handleClosePaintModal,
    handleOpenPaintModal,
    openPaintModal,
    handleChangePaint,
    handleChange,
    paintLead,
    state,
    edit,
    leadTimes,
    region,
    isFetching,
    hasDealerAccess,
    hasAdminAccess,
    isLoading
  } = useLeadTimesSelector();

  if (hasAdminAccess) {
    return (
      <StyledWrapDiv>
        <PaintLeadTimesModal
          open={openPaintModal}
          handleClose={handleClosePaintModal}
          handleChange={handleChangePaint}
          state={paintLead}
          handleSubmit={handleAdminPaintLeadTimeUpdate}
        />

        <StyledEditIcon onClick={changeToEditMode} size="small">
          <EditIcon color="primary" />
        </StyledEditIcon>

        <StyledPaintIcon onClick={handleOpenPaintModal} size="small">
          <PaintIcon color="primary" />
        </StyledPaintIcon>

        {edit && (
          <>
            <StyledUndoTypo>Undo</StyledUndoTypo>

            <StyledBtn
              size="small"
              onClick={handleAdminLeadTimeUpdate}
              color="customGrey"
              variant="contained"
            >
              Update
            </StyledBtn>
          </>
        )}
        <StyledRowDiv>
          <StyledTypographyTitle variant="h4">
            Current Lead Times
          </StyledTypographyTitle>
        </StyledRowDiv>
        <StyledRowDiv2>
          {!isFetching ? (
            leadTimes.map((door: LeadTimeObj, index: number) => (
              <StyledColDiv key={door.doorType}>
                <StyledRowDiv3>
                  <StyledTypoHeader variant="body1">
                    {door.doorType === "Bi-Fold"
                      ? "Bi-Fold Doors/Windows"
                      : `${door.doorType} Doors`}
                  </StyledTypoHeader>
                </StyledRowDiv3>

                <StyledImageWrap>
                  {door.doorType.includes("Bi-Fold") && (
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={publicIds.bifoldPID}
                      style={{
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                      }}
                    />
                  )}
                  {door.doorType === "Slider" && (
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={publicIds.sliderPID}
                      style={{
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                      }}
                    />
                  )}
                  {door.doorType === "Swing" && (
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={publicIds.swingPID}
                      style={{
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                      }}
                    />
                  )}
                  {door.doorType === "Pivot" && (
                    <Image
                      cloudName="ag-millworks"
                      secure="true"
                      publicId={publicIds.pivotPID}
                      style={{
                        width: "auto",
                        height: "auto",
                        maxWidth: "100%",
                      }}
                    />
                  )}
                </StyledImageWrap>

                <StyledLeadTimeRow style={{ marginTop: 10 }}>
                  <StyledSCPositionTypo variant="subtitle1">
                    {index === 0 || !edit ? "SC:" : ""}
                  </StyledSCPositionTypo>

                  <StyledSCDiv>
                    {edit && (
                      <StyledInput
                        onChange={handleChange}
                        value={
                          door.doorType.includes("Bi-Fold")
                            ? state.bifold
                            : door.doorType === "Slider"
                            ? state.slider
                            : door.doorType === "Swing"
                            ? state.swing
                            : door.doorType === "Pivot"
                            ? state.pivot
                            : ""
                        }
                        name={door.doorType.replace("-", "").toLowerCase()}
                        placeholder={door.leadTime}
                      />
                    )}
                    {!edit && (
                      <Typography style={{ padding: 5 }} variant="body2">
                        {region === "NorCal"
                          ? `Mid ${door.leadTimeNorthMonth}`
                          : door.leadTime}
                      </Typography>
                    )}
                  </StyledSCDiv>
                </StyledLeadTimeRow>

                <StyledLeadTimeRow style={{ marginTop: 7 }}>
                  <StyledSCPositionTypo variant="subtitle1">
                    {index === 0 || !edit ? "NC:" : ""}
                  </StyledSCPositionTypo>

                  <StyledSCDiv>
                    {edit ? (
                      <NorthLeadTimeSelect
                        leadTime={
                          door.doorType.includes("Bi-Fold") && state.bifoldNorth
                            ? state.bifoldNorth
                            : door.doorType === "Slider" && state.sliderNorth
                            ? state.sliderNorth
                            : door.doorType === "Swing" && state.swingNorth
                            ? state.swingNorth
                            : door.doorType === "Pivot" && state.pivotNorth
                            ? state.pivotNorth
                            : ""
                        }
                        handleChange={handleChange}
                        name={`${door.doorType
                          .replace("-", "")
                          .toLowerCase()}North`}
                      />
                    ) : (
                      <Typography style={{ padding: 5 }} variant="body2">
                        {door.leadTimeNorthMonth}
                      </Typography>
                    )}
                  </StyledSCDiv>
                </StyledLeadTimeRow>
              </StyledColDiv>
            ))
          ) : (
            <>
              <StyledColDiv style={{ width: "100%" }}>
                <StyledRowDiv2
                  style={{ width: "100%", justifyContent: "space-between" }}
                >
                  {[0, 1, 2, 3].map((itm) => (
                    <div
                      key={itm}
                      style={{
                        padding: 10,
                        width: "100%", // or 25% if 4 per row
                        maxWidth: 150, // optional: cap the size
                        aspectRatio: "3 / 5", // maintain height-to-width ratio
                      }}
                    >
                      <Skeleton
                        animation="wave"
                        variant="rectangular"
                        width="100%"
                        height="100%"
                      />
                    </div>
                  ))}
                </StyledRowDiv2>
              </StyledColDiv>
            </>
          )}
        </StyledRowDiv2>

        <StyledRowDiv style={{ flexDirection: "column", padding: 10 }}>
          <Typography variant="caption">
            Note: Lead Times shown are for standard orders and are subject to
            change at anytime.
          </Typography>
          <Typography variant="caption">
            Does not include Paint/Stain or Freight Lead Time.
          </Typography>
        </StyledRowDiv>
      </StyledWrapDiv>
    );
  }

  return (
    <StyledWrapDiv>
      <StyledRowDiv>
        <StyledTypographyTitle variant="h4">
          Current Lead Times
        </StyledTypographyTitle>
      </StyledRowDiv>
      <StyledRowDiv2>
        {!isLoading ? (
          leadTimes.map((door: LeadTimeObj) => (
            <StyledColDiv key={door.doorType}>
              <StyledRowDiv3>
                <StyledTypoHeader variant="body1">
                  {door.doorType === "Bi-Fold"
                    ? "Bi-Fold Doors/Windows"
                    : `${door.doorType} Doors`}
                </StyledTypoHeader>
              </StyledRowDiv3>

              <StyledImageWrap>
                {door.doorType.includes("Bi-Fold") && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={publicIds.bifoldPID}
                    style={{ width: "auto", height: "auto", maxWidth: "100%" }}
                  />
                )}
                {door.doorType === "Slider" && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={publicIds.sliderPID}
                    style={{ width: "auto", height: "auto", maxWidth: "100%" }}
                  />
                )}
                {door.doorType === "Swing" && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={publicIds.swingPID}
                    style={{ width: "auto", height: "auto", maxWidth: "100%" }}
                  />
                )}
                {door.doorType === "Pivot" && (
                  <Image
                    cloudName="ag-millworks"
                    secure="true"
                    publicId={publicIds.pivotPID}
                    style={{ width: "auto", height: "auto", maxWidth: "100%" }}
                  />
                )}
              </StyledImageWrap>

              <StyledSCDiv>
                {hasDealerAccess && (
                  <Typography style={{ padding: 5 }} variant="body2">
                    {region === "NorCal"
                      ? `Mid ${door.leadTimeNorthMonth}`
                      : door.leadTime}
                  </Typography>
                )}
              </StyledSCDiv>
            </StyledColDiv>
          ))
        ) : (
          <>
            <StyledColDiv style={{ width: "100%" }}>
              <StyledRowDiv2
                style={{ width: "100%", justifyContent: "space-between" }}
              >
                {[0, 1, 2, 3].map((itm) => (
                  <div
                    key={itm}
                    style={{
                      padding: 10,
                      width: "100%", // or 25% if 4 per row
                      maxWidth: 150, // optional: cap the size
                      aspectRatio: "3 / 5", // maintain height-to-width ratio
                    }}
                  >
                    <Skeleton
                      animation="wave"
                      variant="rectangular"
                      width="100%"
                      height="100%"
                    />
                  </div>
                ))}
              </StyledRowDiv2>
            </StyledColDiv>
          </>
        )}
      </StyledRowDiv2>

      <StyledRowDiv style={{ flexDirection: "column", padding: 10 }}>
        <Typography variant="caption">
          Note: Lead Times shown are for standard orders and are subject to
          change at anytime.
        </Typography>
        <Typography variant="caption">Does not include Paint/Stain or Freight Lead Time.</Typography>
      </StyledRowDiv>
    </StyledWrapDiv>
  );
};

export default LeadTimes;

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

interface NorthLeadTimeSelectProps {
  leadTime: string;
  handleChange: (event: SelectChangeEvent<string>) => void;
  name: string;
}

const NorthLeadTimeSelect: FC<NorthLeadTimeSelectProps> = ({
  leadTime,
  handleChange,
  name,
}) => {
  return (
    <Select
      style={{ fontSize: 12, width: 102 }}
      value={leadTime}
      name={name}
      onChange={handleChange}
      size="small"
    >
      {months.map((month) => (
        <MenuItem key={month} value={month}>
          {month}
        </MenuItem>
      ))}
    </Select>
  );
};
